import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { DeviceMobile, DownloadSimple, X, ShareNetwork, Plus } from "@phosphor-icons/react";

/**
 * Two-in-one installer:
 *  - Chrome/Edge/Android : capture `beforeinstallprompt` and show a nice CTA button
 *  - iOS Safari : show a bottom banner explaining "Partager → Sur l'écran d'accueil"
 * Auto-hides once the app is installed (display-mode: standalone) or the user dismissed today.
 */
const DISMISS_KEY = "helios.install.dismissedAt";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
}
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function dismissedRecently() {
  const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return t && Date.now() - t < 7 * 24 * 60 * 60 * 1000; // 7 jours
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(dismissedRecently());
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    if (installed || dismissed) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [installed, dismissed]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "dismissed") dismiss();
    setDeferred(null);
  };

  if (installed || dismissed) return null;

  // Chrome/Edge/Android — inline card CTA
  if (deferred) {
    return (
      <Card className="border-primary/30 bg-primary/5" data-testid="install-cta">
        <CardContent className="pt-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <DownloadSimple size={22} weight="duotone"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium">Installez MasterTicket sur votre appareil</div>
            <div className="text-xs text-muted-foreground mt-0.5">Ouverture instantanée, notifications push, expérience plein écran — sans passer par le navigateur.</div>
          </div>
          <Button onClick={install} data-testid="install-btn">
            <DownloadSimple size={14} className="mr-1.5"/> Installer
          </Button>
          <button onClick={dismiss} className="p-1 text-muted-foreground hover:text-foreground" aria-label="Ignorer" data-testid="install-dismiss">
            <X size={16}/>
          </button>
        </CardContent>
      </Card>
    );
  }

  // iOS Safari — bottom banner (beforeinstallprompt not available)
  if (isIOS()) {
    return (
      <Card className="border-primary/30 bg-primary/5" data-testid="install-ios-banner">
        <CardContent className="pt-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <DeviceMobile size={22} weight="duotone"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium">Ajoutez MasterTicket à votre écran d&apos;accueil</div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
              Touchez <ShareNetwork size={13} className="inline mx-0.5"/> puis <Plus size={13} className="inline mx-0.5"/> <b>« Sur l&apos;écran d&apos;accueil »</b> pour l&apos;installer.
            </div>
          </div>
          <button onClick={dismiss} className="p-1 text-muted-foreground hover:text-foreground" aria-label="Ignorer" data-testid="install-dismiss-ios">
            <X size={16}/>
          </button>
        </CardContent>
      </Card>
    );
  }

  return null;
}
